// src/components/Pages/SpecialOffers/QuoteDialog.js
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { processHtmlContent } from "../../../utils/utils";

const QuoteDialog = ({
  open,
  onClose,
  confirmationMessage,
  confirmationIsError,
  selectedVendor,
  selectedCategory,
  handleAcceptQuote, // will be called with confirmationMessage (contains callbackRef)
  loading,
  isMobile,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const dialogStyle = {
    "& .MuiDialog-paper": {
      width: isMobile ? "100vw" : isTablet ? "80vw" : "min(560px, 92vw)",
      maxWidth: isMobile ? "none" : "720px",
      borderRadius: isMobile ? 0 : "8px",
      p: { xs: 2, sm: 2.5 },
      maxHeight: isMobile ? "90vh" : "75vh",
      margin: isMobile ? 0 : "24px",
      bgcolor: "background.paper",
      boxShadow: isMobile ? 0 : 24,
      "& .MuiDialogContent-root": {
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "grey.400", borderRadius: "4px" },
        "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
      },
    },
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={dialogStyle}>
      {isMobile && !loading && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "grey.500",
            bgcolor: "grey.100",
            "&:hover": { bgcolor: "grey.200" },
            borderRadius: "50%",
            p: 0.8,
            zIndex: 1,
          }}
          aria-label="Close dialog"
        >
          <CloseIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }} />
        </IconButton>
      )}

      <DialogTitle
        sx={{
          fontSize: { xs: "1.1rem", sm: "1.2rem" },
          fontWeight: 600,
          pr: isMobile ? 4 : 0,
          pb: 1,
        }}
      >
        {confirmationIsError
          ? "Something went wrong"
          : typeof confirmationMessage === "string"
          ? "Thank You"
          : "Quote Details"}
      </DialogTitle>

      <DialogContent sx={{ py: { xs: 0.5, sm: 0.75 } }}>
        {typeof confirmationMessage === "string" ? (
          <Typography
            color="text.primary"
            sx={{ 
              fontSize: { xs: "0.75rem", sm: "0.8rem" }, 
              lineHeight: 1.4,
              '& a': {
                color: 'rgb(33, 173, 147)',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
            dangerouslySetInnerHTML={{
              __html: processHtmlContent(confirmationMessage),
            }}
          />
        ) : (
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                fontWeight: 600,
                mb: 1,
                textAlign: isMobile ? "center" : "left",
              }}
            >
              Your Quick Quote from {selectedVendor?.name || 'iWYZE'}
            </Typography>

            <Table sx={{ bgcolor: "grey.50", borderRadius: "6px", overflow: "hidden", width: "100%" }}>
              <TableBody>
                <TableRow sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "grey.200" } }}>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, fontWeight: 600, p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    Vehicle Details
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, color: "text.secondary", p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    {confirmationMessage.vehicleDetails}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "grey.200" } }}>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, fontWeight: 600, p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    Type of Cover
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, color: "text.secondary", p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    {confirmationMessage.coverType}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "grey.200" } }}>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, fontWeight: 600, p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    Monthly Premium
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, color: "text.secondary", p: { xs: 0.5, sm: 1 }, borderBottom: "none" }}>
                    {confirmationMessage.monthlyPremium}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {!confirmationIsError && (
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: "text.secondary",
                  mt: 1.5,
                  mb: 1,
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                This is an obligation-free quote. By clicking accept, your contact
                details will be sent to {selectedVendor?.name || 'iWYZE'} for a{" "}
                {selectedCategory ? selectedCategory.replace(/-/g, " ") : "insurance"}{" "}
                consultant to contact you and discuss your quote. The premium shown in this quotation is indicative only and based on the information currently available. The final premium may change and will depend on the confirmed risk profile. Terms and conditions apply.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: isMobile ? "center" : "flex-end", gap: 1 }}>
        {confirmationIsError || typeof confirmationMessage === "string" ? (
          <Button
            onClick={handleClose}
            color="primary"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              py: { xs: 0.8, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 500,
              color: "#fff",
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
              boxShadow: "none",
            }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button
              onClick={() => handleAcceptQuote?.(confirmationMessage)}
              disabled={loading}
              color="primary"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                py: { xs: 0.8, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 500,
                color: "#fff !important",
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
                boxShadow: "none",
              }}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
            >
              {loading ? "Submitting..." : "Accept Quote"}
            </Button>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                py: { xs: 0.8, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 500,
                color: "rgb(33, 173, 147)",
                bgcolor: "grey.100",
                "&:hover": { bgcolor: "grey.200" },
                boxShadow: "none",
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuoteDialog;
